import { ApexOptions } from 'apexcharts';
import _ from 'lodash';
import moment from 'moment';
import { SeriesItem } from './seriesData';

export const intColor = (int: number, opacity?: string) =>
  _.inRange(int, 4, 7)
    ? opacity
      ? `rgba(210, 161, 6, ${opacity})`
      : '#D2A106'
    : _.inRange(int, 7, 11)
      ? opacity
        ? `rgba(255, 131, 137, ${opacity})`
        : '#FF8389'
      : opacity
        ? 'rgba(8, 189, 186, .1)'
        : '#08BDBA';

let activeChart: string;

export const chartOptions = ({
  end,
  start,
  maxRows,
  colorMode,
  titleText,
  chartIndex,
  chartGroup,
  seriesData,
  showToolbar,
  setSelectedDetectionId,
}: {
  end: number;
  start: number;
  maxRows: number;
  chartIndex: number;
  chartGroup: string;
  colorMode: string;
  titleText: string;
  showToolbar: boolean;
  seriesData: {
    row_count: number;
    series: SeriesItem[];
  };
  setSelectedDetectionId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
}): ApexOptions => {
  const showTooltip = (evt: MouseEvent, text: string) => {
    const tooltip = document.getElementById('tooltip');

    const leftPos =
      evt.pageX + 175 < window.innerWidth ? evt.pageX + 10 : evt.pageX - 175;

    if (tooltip) {
      tooltip.innerHTML = text;
      tooltip.style.visibility = 'visible';
      tooltip.style.opacity = '1';
      tooltip.style.left = leftPos + 'px';
      tooltip.style.top = evt.pageY + 10 + 'px';
    }
  };

  const hideTooltip = () => {
    const tooltip = document.getElementById('tooltip');
    if (tooltip) {
      tooltip.style.visibility = 'hidden';
      tooltip.style.opacity = '0';
    }
  };
  const mountCustomizations = () => {
    const goals = Array.from(
      document?.getElementsByClassName('goal-line'),
    ) as SVGLineElement[];
    goals?.forEach((goal) => {
      if (goal !== null) {
        goal.onclick = function (this) {
          const detectionId = (this as SVGLineElement).dataset.detectionId;
          setSelectedDetectionId(detectionId);
        };
        goal.onmousemove = function (this, event) {
          const tooltipTextStart = (this as SVGLineElement).dataset
            .tooltipTextStart;
          const tooltipTextEnd = (this as SVGLineElement).dataset
            .tooltipTextEnd;
          const tooltipTextAlerts = (this as SVGLineElement).dataset
            .tooltipTextAlerts;
          const tooltipTextAlert = (this as SVGLineElement).dataset
            .tooltipTextAlert;
          const tooltipTextEntity = (this as SVGLineElement).dataset
            .tooltipTextEntity;
          showTooltip(
            event,
            `<div class="detection-tooltip">
              ${tooltipTextStart}<br />${tooltipTextEnd}<br />
              <strong>${tooltipTextAlerts}</strong>
              ${tooltipTextAlert ? `<br /><strong>${tooltipTextAlert}</strong>` : ''}
              ${tooltipTextEntity ? `<br /><strong>${tooltipTextEntity}</strong>` : ''}
            </div>`,
          );
        };
        goal.onmouseout = function (this) {
          hideTooltip();
        };
      }
    });
  };
  return {
    title: {
      floating: true,
      text: `${titleText} (${
        seriesData.row_count <= maxRows ? seriesData.row_count : maxRows
      }/${seriesData.row_count})`,
      style: {
        color: colorMode === 'light' ? '#333333' : '#FFFFFF',
        fontSize: '14px',
        fontWeight: 600,
      },
    },
    tooltip: {
      shared: false,
      cssClass: `chart-${chartIndex}-tooltip`,
      custom: function ({ seriesIndex, dataPointIndex, w }) {
        const data =
          w?.globals?.initialSeries?.[seriesIndex]?.data?.[dataPointIndex];
        return activeChart === `chart-${chartIndex}`
          ? `<div class="detection-tooltip">
          ${moment(data?.y?.[0]).format('lll')}<br />${moment(
            data?.y?.[1],
          ).format('lll')}<br />
          <strong>${data?.alert_name}</strong><br /><strong>${
            data?.entity_name
          }</strong></div>`
          : '';
      },
    },
    colors: seriesData.series
      .filter((series) => series.name !== '')
      .map((series) =>
        series?.name
          ? intColor(series.name.split(' ')[0] as unknown as number, '0.1')
          : '',
      ),
    states: {
      normal: {
        filter: { type: 'none', value: 0 },
      },
      hover: {
        filter: { type: 'lighten', value: 0.5 },
      },
      active: {
        filter: { type: 'darken', value: 0.2 },
        allowMultipleDataPointsSelection: false,
      },
    },
    chart: {
      group: chartGroup,
      id: `chart-${chartIndex}`,
      animations: {
        enabled: false,
      },
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: false,
      },
      events: {
        mounted: function (_chartContext, _config) {
          mountCustomizations();
        },
        mouseMove: function (_event, chartContext, _opts) {
          activeChart = chartContext?.w?.globals?.chartID;
        },
        click: (_chart, _options) => {
          mountCustomizations();
        },
        dataPointSelection: (_e, chart, options) => {
          setSelectedDetectionId(
            chart.opts.series[options.seriesIndex].data[options.dataPointIndex]
              ?.detection_id,
          );
        },
        zoomed: function (_chartContext) {
          mountCustomizations();
        },
        scrolled: function (_chartContext) {
          mountCustomizations();
        },
      },
      type: 'rangeBar',
      toolbar: {
        autoSelected: 'pan',
        show: true,
        offsetX: showToolbar ? 0 : -10000,
        offsetY: 0,
        tools: {
          download: false,
          pan: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M408.781 128.007C386.356 127.578 368 146.36 368 168.79V256h-8V79.79c0-22.43-18.356-41.212-40.781-40.783C297.488 39.423 280 57.169 280 79v177h-8V40.79C272 18.36 253.644-.422 231.219.007 209.488.423 192 18.169 192 40v216h-8V80.79c0-22.43-18.356-41.212-40.781-40.783C121.488 40.423 104 58.169 104 80v235.992l-31.648-43.519c-12.993-17.866-38.009-21.817-55.877-8.823-17.865 12.994-21.815 38.01-8.822 55.877l125.601 172.705A48 48 0 0 0 172.073 512h197.59c22.274 0 41.622-15.324 46.724-37.006l26.508-112.66a192.011 192.011 0 0 0 5.104-43.975V168c.001-21.831-17.487-39.577-39.218-39.993z"/></svg>`,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '50%',
        rangeBarGroupRows: true,
      },
    },
    yaxis: {
      labels: {
        minWidth: 250,
        maxWidth: 250,
        align: 'left',
        style: {
          colors: colorMode === 'light' ? '#333333' : '#FFFFFF',
          fontSize: '12px',
        },
        // We need to truncate long labels to prevent layout issues
        formatter: (val: number) => {
          const str = String(val);
          return str.length > 50 ? str.slice(0, 50) + '…' : str;
        },
      },
    },
    xaxis: {
      type: 'datetime',
      labels: {
        show: false,
        style: {
          colors: colorMode === 'light' ? '#333333' : '#FFFFFF',
        },
      },
      axisBorder: {
        show: false,
        color: '#525252',
      },
      axisTicks: {
        show: false,
        color: '#525252',
      },
      min: start,
      max: end,
      title: {
        style: {
          color: colorMode === 'light' ? '#333333' : '#FFFFFF',
        },
      },
    },
    stroke: {
      width: 1,
    },
    fill: {
      type: 'solid',
      opacity: 0.6,
    },
    legend: {
      show: false,
      position: 'left',
      horizontalAlign: 'left',
      showForNullSeries: false,
      labels: {
        colors: colorMode === 'light' ? '#333333' : '#FFFFFF',
      },
    },
    grid: {
      show: false,
      borderColor: '#525252',
    },
    series: seriesData.series as unknown as ApexAxisChartSeries,
  };
};
